import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { CircularProgress, InputAdornment } from "@mui/material";
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// MUI Icons for Adornments
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PercentIcon from '@mui/icons-material/Percent';
import EventIcon from '@mui/icons-material/Event';

export default function CouponAddModel({ open, handleClose, existingCoupons = [] }) {
    const [input, setInput] = useState({
        coupon_id: "",
        percentage: "",
        start_date: null,
        end_date: null,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setInput({
                coupon_id: "",
                percentage: "",
                start_date: null,
                end_date: null,
            });
            setErrors({});
            setLoading(false); // Ensure loading state is reset when opening
        }
    }, [open]);

    const validateField = (name, value) => {
        let error = "";
        const trimmedValue = typeof value === 'string' ? value.trim() : value;

        switch (name) {
            case "coupon_id":
                if (!trimmedValue) {
                    error = "Coupon Code is required.";
                } else if (existingCoupons.some(coupon => coupon.coupon_id.toLowerCase() === trimmedValue.toLowerCase())) {
                    error = "Coupon Code already exists. Please choose a unique one.";
                }
                break;
            case "percentage":
                const parsedPercentage = parseFloat(value);
                if (isNaN(parsedPercentage) || parsedPercentage < 0 || parsedPercentage > 100) {
                    error = "Discount must be between 0 and 100.";
                }
                break;
            case "start_date":
                if (!value) {
                    error = "Start Date is required.";
                } else {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0); // Normalize to start of today

                    const selectedStartDate = new Date(value);
                    selectedStartDate.setHours(0, 0, 0, 0); // Normalize selected date

                    if (selectedStartDate < today) {
                        error = "Start Date cannot be in the past.";
                    }
                }
                break;
            case "end_date":
                if (!value) {
                    error = "End Date is required.";
                } else if (input.start_date) {
                    const selectedEndDate = new Date(value);
                    selectedEndDate.setHours(0, 0, 0, 0); // Normalize selected date

                    const selectedStartDate = new Date(input.start_date);
                    selectedStartDate.setHours(0, 0, 0, 0); // Normalize start date

                    if (selectedEndDate < selectedStartDate) {
                        error = "End Date cannot be before Start Date.";
                    }
                }
                break;
            default:
                break;
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const processedValue = name === "percentage" && value !== "" ? parseFloat(value) : value;

        setInput((prev) => ({ ...prev, [name]: processedValue }));
        setErrors((prev) => ({ ...prev, [name]: validateField(name, processedValue) }));
    };

    const handleDateChange = (date, name) => {
        setInput((prev) => ({ ...prev, [name]: date }));
        setErrors((prev) => {
            const newErrors = { ...prev, [name]: validateField(name, date) };
            if (name === "start_date") {
                // Re-validate end_date if start_date changes
                newErrors.end_date = validateField("end_date", input.end_date);
            } else if (name === "end_date") {
                // Re-validate start_date if end_date changes
                newErrors.start_date = validateField("start_date", input.start_date);
            }
            return newErrors;
        });
    };

    const validateForm = () => {
        let newErrors = {};
        let isValid = true;

        const fieldsToValidate = ['coupon_id', 'percentage', 'start_date', 'end_date'];
        fieldsToValidate.forEach(name => {
            const error = validateField(name, input[name]);
            if (error) {
                newErrors[name] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please correct the highlighted errors in the form.',
            });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...input,
                percentage: parseFloat(input.percentage),
                start_date: input.start_date ? input.start_date.toISOString().substring(0, 10) : null,
                end_date: input.end_date ? input.end_date.toISOString().substring(0, 10) : null,
            };

            const response = await api.post("/coupons/", payload);

            if (response.data.status === 1) {
                Swal.fire({
                    icon: 'success',
                    title: 'Coupon Added!',
                    text: response.data.message,
                    timer: 2000,
                    showConfirmButton: false,
                });
                handleClose();
            } else {
                const errorMessage = response.data.message && (response.data.message.includes("duplicate key value") || response.data.message.includes("already exists"))
                    ? "Coupon Code already exists. Please use a different one."
                    : response.data.message || "Failed to add coupon.";
                Swal.fire({
                    icon: 'error',
                    title: 'Failed to Add Coupon',
                    text: errorMessage,
                });
            }
        } catch (err) {
            console.error("Error adding coupon:", err.response || err);
            const errorMessage = err.response?.data?.message || "An unexpected error occurred while adding the coupon.";
            Swal.fire({
                icon: 'error',
                title: 'API Error',
                text: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <form onSubmit={handleSubmit}>
                <DialogTitle
                    sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1rem",
                        py: 2,
                        mb: 2,
                    }}
                >
                    Add New Coupon
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: '#f5f5f5' }}>
                    <MDBox pt={1}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Coupon Code"
                                    name="coupon_id"
                                    value={input.coupon_id}
                                    onChange={handleChange}
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    error={!!errors.coupon_id}
                                    helperText={errors.coupon_id}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocalOfferIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Discount Percentage"
                                    name="percentage"
                                    value={input.percentage}
                                    onChange={handleChange}
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    error={!!errors.percentage}
                                    helperText={errors.percentage}
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PercentIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ReactDatePicker
                                    selected={input.start_date}
                                    onChange={(date) => handleDateChange(date, "start_date")}
                                    dateFormat="yyyy-MM-dd"
                                    customInput={
                                        <TextField
                                            label="Start Date"
                                            fullWidth
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            error={!!errors.start_date}
                                            helperText={errors.start_date}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EventIcon color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    }
                                    minDate={new Date()} // Prevent selecting past dates from current day
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ReactDatePicker
                                    selected={input.end_date}
                                    onChange={(date) => handleDateChange(date, "end_date")}
                                    dateFormat="yyyy-MM-dd"
                                    customInput={
                                        <TextField
                                            label="End Date"
                                            fullWidth
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            error={!!errors.end_date}
                                            helperText={errors.end_date}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EventIcon color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    }
                                    // End date cannot be before start date, or before today if start_date is not set
                                    minDate={input.start_date || new Date()}
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px', backgroundColor: '#e3f2fd' }}>
                    <MDButton onClick={handleClose} color="secondary" disabled={loading} sx={{ mr: 1 }}>
                        Cancel
                    </MDButton>
                    <MDButton type="submit" color="success" disabled={loading}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Save"}
                    </MDButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}